export * from "./types";
export * from "./constants";
export * from "./predictor";
export * from "./state-updater";
export * from "./session-builder";

import { prisma } from "@/lib/db";
import { checkFocusMastery, getSetCounts, promoteFocusSet } from "./predictor";
import { checkAnswer, updateStateAfterAnswer } from "./state-updater";
import type { FocusSetStatus, PresentedType } from "./types";

export interface AnswerInput {
  userId: string;
  problemId: string;
  sessionId: string;
  userInput: string;
  responseMs: number;
  presentedType: PresentedType;
}

export interface RecordAnswerResult {
  wasCorrect: boolean;
  correctAnswer: string;
  answerDescription: string;
  focusStatus: FocusSetStatus;
  justMastered: boolean;
  newFocusCount: number;
}

/**
 * Record an answer and update all relevant state
 */
export async function recordAnswer(
  input: AnswerInput
): Promise<RecordAnswerResult> {
  // Get problem
  const problem = await prisma.problem.findUnique({
    where: { id: input.problemId },
  });

  if (!problem) {
    throw new Error(`Problem not found: ${input.problemId}`);
  }

  // Determine correctness
  const wasCorrect = checkAnswer(input.userInput, problem.answer);

  // Update problem state
  await updateStateAfterAnswer(input.userId, input.problemId, wasCorrect);

  // Create review log
  await prisma.reviewLog.create({
    data: {
      userId: input.userId,
      problemId: input.problemId,
      sessionId: input.sessionId,
      presentedType: input.presentedType,
      userInput: input.userInput,
      wasCorrect,
      grade: wasCorrect ? "good" : "again",
      responseMs: input.responseMs,
    },
  });

  // Update session stats
  await prisma.studySession.update({
    where: { id: input.sessionId },
    data: {
      completedCount: { increment: 1 },
      correctCount: wasCorrect ? { increment: 1 } : undefined,
    },
  });

  // Check for mastery
  const { mastered, correctCount, totalCount } = await checkFocusMastery(input.userId);
  let justMastered = false;
  let newFocusCount = 0;

  if (mastered) {
    // Promote Focus set to Old and load new Focus set
    newFocusCount = await promoteFocusSet(input.userId);
    justMastered = true;
  }

  // Get updated counts
  const counts = await getSetCounts(input.userId);
  const masteryPercent = counts.focusCount > 0
    ? Math.round((counts.focusCorrectCount / counts.focusCount) * 100)
    : 0;

  return {
    wasCorrect,
    correctAnswer: problem.answer,
    answerDescription: problem.answerDescription,
    focusStatus: {
      ...counts,
      masteryPercent,
      mastered: justMastered,
    },
    justMastered,
    newFocusCount,
  };
}

/**
 * Get user's study statistics
 */
export async function getUserStats(userId: string) {
  const now = new Date();
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);

  const [totalProblems, counts, todayLogs] = await Promise.all([
    prisma.problem.count(),
    getSetCounts(userId),
    prisma.reviewLog.findMany({
      where: { userId, timestamp: { gte: startOfToday } },
    }),
  ]);

  const todayCorrect = todayLogs.filter(l => l.wasCorrect).length;
  const todayTotal = todayLogs.length;

  return {
    totalProblems,
    newCount: counts.newCount,
    focusCount: counts.focusCount,
    focusCorrectCount: counts.focusCorrectCount,
    oldCount: counts.oldCount,
    masteryPercent: counts.focusCount > 0
      ? Math.round((counts.focusCorrectCount / counts.focusCount) * 100)
      : 0,
    todayStats: {
      answered: todayTotal,
      correct: todayCorrect,
      accuracy: todayTotal > 0 ? todayCorrect / todayTotal : 0,
    },
  };
}
